import {
  WEBHOOK_DELIVERY_STATUSES,
  type WebhookDeliveryStatus,
} from '@monorepo/shared/schemas/common'
import type {
  WebhookDelivery,
  WebhookResponse,
} from '@monorepo/shared/schemas/webhooks'
import type { ColumnDef } from '@tanstack/react-table'
import {
  AlertCircle,
  CheckCircle,
  Clock,
  Code,
  Filter,
  RefreshCw,
  RotateCcw,
  XCircle,
} from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { VirtualizedTable } from '@/shared/components/table/VirtualizedTable'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Label } from '@/shared/components/ui/label'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { extractErrorMessage } from '@/shared/lib/error-utils'
import { toast } from '@/shared/lib/toast'
import { webhookService } from '../services/webhookService'

interface WebhookLogsTabProps {
  webhooks: WebhookResponse[]
}

interface DeliveryFilters {
  status?: WebhookDeliveryStatus
  webhookId?: string
  page?: number
}

export function WebhookLogsTab({ webhooks }: WebhookLogsTabProps) {
  const [deliveries, setDeliveries] = useState<WebhookDelivery[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedLog, setSelectedLog] = useState<WebhookDelivery | null>(null)
  const [filters, setFilters] = useState<DeliveryFilters>({})
  const [hasNextPage, setHasNextPage] = useState(false)
  const [isFetchingNextPage, setIsFetchingNextPage] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)

  // Filter states
  const [showFilterPopover, setShowFilterPopover] = useState(false)
  const [tempStatusFilter, setTempStatusFilter] = useState<
    WebhookDeliveryStatus | 'all'
  >('all')
  const [tempWebhookFilter, setTempWebhookFilter] = useState<string | 'all'>(
    'all'
  )

  const loadDeliveries = useCallback(async () => {
    setLoading(true)
    try {
      // Load deliveries from all webhooks or a specific one
      const webhookId = filters.webhookId
      if (webhookId) {
        const result = await webhookService.getDeliveries(webhookId, {
          page: 1,
          limit: 50,
          status: filters.status,
        })
        setDeliveries(result.deliveries)
        setHasNextPage(result.pagination.page < result.pagination.total_pages)
        setCurrentPage(1)
      } else {
        // Load from all webhooks
        const allDeliveries: WebhookDelivery[] = []
        for (const webhook of webhooks) {
          try {
            const result = await webhookService.getDeliveries(webhook.id, {
              page: 1,
              limit: 20,
              status: filters.status,
            })
            allDeliveries.push(...result.deliveries)
          } catch {
            // Skip webhooks that fail
          }
        }
        // Sort by created_at descending
        allDeliveries.sort(
          (a, b) =>
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        )
        setDeliveries(allDeliveries)
        setHasNextPage(false)
      }
    } catch (error) {
      toast.error(extractErrorMessage(error))
    } finally {
      setLoading(false)
    }
  }, [filters, webhooks])

  const fetchNextPage = async () => {
    if (!filters.webhookId) return
    setIsFetchingNextPage(true)
    try {
      const nextPage = currentPage + 1
      const result = await webhookService.getDeliveries(filters.webhookId, {
        page: nextPage,
        limit: 50,
        status: filters.status,
      })
      setDeliveries(prev => [...prev, ...result.deliveries])
      setCurrentPage(nextPage)
      setHasNextPage(nextPage < result.pagination.total_pages)
    } catch (error) {
      toast.error(extractErrorMessage(error))
    } finally {
      setIsFetchingNextPage(false)
    }
  }

  useEffect(() => {
    loadDeliveries()
  }, [loadDeliveries])

  const applyFilters = () => {
    setFilters({
      status: tempStatusFilter === 'all' ? undefined : tempStatusFilter,
      webhookId: tempWebhookFilter === 'all' ? undefined : tempWebhookFilter,
    })
    setShowFilterPopover(false)
  }

  const clearFilters = () => {
    setTempStatusFilter('all')
    setTempWebhookFilter('all')
  }

  const openFilterPopover = () => {
    setTempStatusFilter(filters.status || 'all')
    setTempWebhookFilter(filters.webhookId || 'all')
    setShowFilterPopover(true)
  }

  const getStatusVariant = (status: WebhookDeliveryStatus) => {
    switch (status) {
      case 'delivered':
        return 'default' as const
      case 'failed':
        return 'destructive' as const
      case 'pending':
        return 'secondary' as const
      case 'cancelled':
        return 'outline' as const
    }
  }

  const getStatusIcon = (status: WebhookDeliveryStatus) => {
    switch (status) {
      case 'delivered':
        return <CheckCircle className='w-4 h-4' />
      case 'failed':
        return <AlertCircle className='w-4 h-4' />
      case 'pending':
        return <Clock className='w-4 h-4' />
      case 'cancelled':
        return <XCircle className='w-4 h-4' />
    }
  }

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
  }

  // Find the webhook description for a given delivery
  const getWebhookName = (webhookId: string) => {
    const webhook = webhooks.find(w => w.id === webhookId)
    return webhook?.description || webhook?.url || webhookId
  }

  const columns: ColumnDef<WebhookDelivery>[] = [
    {
      accessorKey: 'created_at',
      header: 'Time',
      cell: ({ row }) => {
        const log = row.original
        return (
          <div className='text-sm'>
            <div className='font-medium'>{formatDateTime(log.created_at)}</div>
            {log.response_time_ms != null && (
              <div className='text-xs text-muted-foreground'>
                {log.response_time_ms}ms
              </div>
            )}
          </div>
        )
      },
      size: 350,
    },
    {
      accessorKey: 'webhook_id',
      header: 'Webhook',
      cell: ({ row }) => {
        const log = row.original
        return (
          <div className='text-sm'>
            <div className='font-medium truncate'>
              {getWebhookName(log.webhook_id)}
            </div>
            <div className='text-xs text-muted-foreground truncate'>
              {log.webhook_id}
            </div>
          </div>
        )
      },
      size: 400,
    },
    {
      accessorKey: 'event_type',
      header: 'Event',
      cell: ({ row }) => {
        const log = row.original
        return (
          <div className='text-sm'>
            <div className='font-medium truncate'>{log.event_type}</div>
            <div className='text-xs text-muted-foreground'>
              Attempt {log.retry_count + 1}
            </div>
          </div>
        )
      },
      size: 400,
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const log = row.original
        return (
          <div className='flex items-center gap-2'>
            <Badge
              variant={getStatusVariant(log.status)}
              className='flex items-center gap-1'
            >
              {getStatusIcon(log.status)}
              {log.status.charAt(0).toUpperCase() + log.status.slice(1)}
            </Badge>
            {log.http_status && (
              <span className='text-xs text-muted-foreground'>
                {log.http_status}
              </span>
            )}
          </div>
        )
      },
      size: 380,
    },
  ]

  return (
    <div className='space-y-6'>
      {/* Filters */}
      <div className='flex items-center justify-between'>
        <Popover open={showFilterPopover} onOpenChange={setShowFilterPopover}>
          <PopoverTrigger asChild>
            <Button
              variant='outline'
              size='sm'
              onClick={openFilterPopover}
              className='flex items-center gap-2'
            >
              <Filter className='w-4 h-4' />
              Filters
            </Button>
          </PopoverTrigger>
          <PopoverContent className='w-96' align='start'>
            <div className='space-y-4'>
              <div className='space-y-2'>
                <h4 className='font-medium leading-none'>Filter Logs</h4>
                <p className='text-sm text-muted-foreground'>
                  Apply filters to refine your webhook logs view
                </p>
              </div>

              <div className='space-y-4'>
                <div className='space-y-2'>
                  <Label>Webhook</Label>
                  <Select
                    value={tempWebhookFilter}
                    onValueChange={setTempWebhookFilter}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='all'>All Webhooks</SelectItem>
                      {webhooks.map(webhook => (
                        <SelectItem key={webhook.id} value={webhook.id}>
                          {webhook.description || webhook.url}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className='space-y-2'>
                  <Label>Status</Label>
                  <Select
                    value={tempStatusFilter}
                    onValueChange={
                      setTempStatusFilter as (value: string) => void
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='all'>All Status</SelectItem>
                      {WEBHOOK_DELIVERY_STATUSES.map(status => (
                        <SelectItem key={status} value={status}>
                          {status.charAt(0).toUpperCase() + status.slice(1)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className='flex justify-between pt-4'>
                <Button onClick={clearFilters} variant='outline' size='sm'>
                  <RotateCcw className='w-4 h-4 mr-2' />
                  Clear
                </Button>
                <div className='flex gap-2'>
                  <Button
                    variant='outline'
                    size='sm'
                    onClick={() => setShowFilterPopover(false)}
                  >
                    Cancel
                  </Button>
                  <Button onClick={applyFilters} size='sm'>
                    Apply
                  </Button>
                </div>
              </div>
            </div>
          </PopoverContent>
        </Popover>

        <Button
          variant='outline'
          size='sm'
          onClick={loadDeliveries}
          disabled={loading}
        >
          <RefreshCw
            className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`}
          />
          Refresh
        </Button>
      </div>

      {/* Table */}
      <div className='[&_table]:bg-transparent [&_thead]:bg-transparent [&_tr:hover]:bg-slate-50/50 dark:[&_tr:hover]:bg-slate-800/50'>
        <VirtualizedTable
          data={deliveries}
          columns={columns}
          isLoading={loading}
          isFetchingNextPage={isFetchingNextPage}
          hasNextPage={hasNextPage}
          fetchNextPage={fetchNextPage}
          containerHeight='calc(100vh - 380px)'
          emptyMessage='No webhook logs found. Logs will appear here when webhooks are triggered.'
          loadingMessage='Loading more webhook logs...'
          onRowClick={log => setSelectedLog(log)}
          enableSorting={true}
          enableColumnResizing={true}
          enableColumnReordering={true}
          enableColumnPinning={false}
        />
      </div>

      {/* Log Detail Dialog */}
      {selectedLog && (
        <Dialog open={!!selectedLog} onOpenChange={() => setSelectedLog(null)}>
          <DialogContent className='max-w-4xl max-h-[90vh] overflow-y-auto'>
            <DialogHeader>
              <DialogTitle className='flex items-center gap-2'>
                <Code className='w-5 h-5' />
                Webhook Log Details
              </DialogTitle>
              <DialogDescription>
                Event: {selectedLog.event_type} •{' '}
                {formatDateTime(selectedLog.created_at)}
              </DialogDescription>
            </DialogHeader>

            <div className='space-y-6 py-4'>
              {/* Summary */}
              <div className='grid gap-4 md:grid-cols-2'>
                <div className='space-y-3'>
                  <div>
                    <div className='text-sm font-medium'>Status</div>
                    <div className='flex items-center gap-2 mt-1'>
                      <Badge
                        variant={getStatusVariant(selectedLog.status)}
                        className='flex items-center gap-1'
                      >
                        {getStatusIcon(selectedLog.status)}
                        {selectedLog.status.charAt(0).toUpperCase() +
                          selectedLog.status.slice(1)}
                      </Badge>
                      {selectedLog.http_status && (
                        <span className='text-sm text-muted-foreground'>
                          HTTP {selectedLog.http_status}
                        </span>
                      )}
                    </div>
                  </div>

                  <div>
                    <div className='text-sm font-medium'>Webhook</div>
                    <div className='text-sm text-muted-foreground mt-1'>
                      {getWebhookName(selectedLog.webhook_id)} (
                      {selectedLog.webhook_id})
                    </div>
                  </div>

                  <div>
                    <div className='text-sm font-medium'>Retries</div>
                    <div className='text-sm text-muted-foreground mt-1'>
                      {selectedLog.retry_count} retries
                      {selectedLog.next_retry_at && (
                        <span className='block'>
                          Next retry:{' '}
                          {formatDateTime(selectedLog.next_retry_at)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className='space-y-3'>
                  <div>
                    <div className='text-sm font-medium'>Created</div>
                    <div className='text-sm text-muted-foreground mt-1'>
                      {formatDateTime(selectedLog.created_at)}
                    </div>
                  </div>

                  {selectedLog.delivered_at && (
                    <div>
                      <div className='text-sm font-medium'>Delivered</div>
                      <div className='text-sm text-muted-foreground mt-1'>
                        {formatDateTime(selectedLog.delivered_at)}
                      </div>
                    </div>
                  )}

                  {selectedLog.response_time_ms != null && (
                    <div>
                      <div className='text-sm font-medium'>Response Time</div>
                      <div className='text-sm text-muted-foreground mt-1'>
                        {selectedLog.response_time_ms}ms
                      </div>
                    </div>
                  )}

                  {selectedLog.error_message && (
                    <div>
                      <div className='text-sm font-medium text-red-600'>
                        Error
                      </div>
                      <div className='text-sm text-red-600 mt-1 font-mono'>
                        {selectedLog.error_message}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className='flex justify-end'>
              <Button variant='outline' onClick={() => setSelectedLog(null)}>
                Close
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  )
}
